import type { MockMessage, MockMessageMedia } from "@/dummy";
import type { OutboxOp } from "@/lib/outbox-store";
import { sameMessageContent } from "../tabs/messages-tab/merge-messages";

/** The part of a queued "sendMessage" op payload the live buffer needs. */
interface QueuedSendPayload {
  text: string;
  threadKey: string | null;
  media?: unknown[];
}

/** Conversation identity used by the shell: a keyed topic thread, else the main chat. */
export function conversationIdOfThreadKey(threadKey: string | null | undefined): string {
  return threadKey || "main";
}

/**
 * Reconcile the queued "sendMessage" ops into the live buffer of the OPEN
 * conversation: every op that belongs to it gets an optimistic bubble (keyed by
 * `clientKey`) whose delivery status mirrors the queue state. Ops of any other
 * thread are skipped, otherwise a message queued in thread A would render inside
 * thread B (the buffer is shared across threads). Returns `prev` when nothing
 * changed so React can bail out.
 */
export function reconcileOutboxOps(
  prev: MockMessage[],
  ops: readonly OutboxOp[],
  activeConvId: string,
  previewsFor: (clientKey: string, media: never) => MockMessageMedia[],
): MockMessage[] {
  let next = prev;
  let changed = false;
  const ensureCopy = () => {
    if (!changed) {
      next = [...prev];
      changed = true;
    }
  };
  for (const e of ops) {
    if (e.op !== "sendMessage") continue;
    const p = e.payload as QueuedSendPayload;
    if (conversationIdOfThreadKey(p.threadKey) !== activeConvId) continue;
    // `sending` maps to a distinct client-only `SENDING` (not `SENT`) so the
    // bubble's status footer keeps its height through queued -> sending ->
    // failed. `SENT` stays reserved for a truly server-acked send.
    const ds = e.status === "failed" ? "FAILED" : e.status === "sending" ? "SENDING" : "QUEUED";
    const idx = next.findIndex((m) => (m.clientKey ?? m.id) === e.clientKey);
    if (idx === -1) {
      ensureCopy();
      const preview = previewsFor(e.clientKey, p.media as never);
      next.push({
        id: e.clientKey,
        clientKey: e.clientKey,
        direction: "INBOUND",
        text: p.text,
        ageLabel: "",
        sender: { kind: "visitor", name: "" },
        ...(preview.length > 0 ? { media: preview } : {}),
        deliveryStatus: ds,
      });
    } else if (next[idx].deliveryStatus !== ds) {
      ensureCopy();
      next[idx] = { ...next[idx], deliveryStatus: ds };
    }
  }
  return changed ? next : prev;
}

/**
 * Apply a realtime `messageUpdate` (an edit): replace the row with the same id
 * in place, keeping its position and stable `clientKey`. An update for an id we
 * never saw (a history row, or one older than the loaded page) is kept as an `editOnly` overlay:
 * the merge applies it onto the history row with that id (now or once an older page loads) and
 * never renders it as a row of its own.
 */
export function applyMessageUpdate(prev: MockMessage[], msg: MockMessage): MockMessage[] {
  const idx = prev.findIndex((m) => m.id === msg.id);
  // Idempotent: reactions, labels and enrichment also arrive as `messageUpdate`; one that changes nothing
  // visible (or a repeat) must not mark the row edited or re-render it.
  if (idx !== -1 && sameMessageContent(prev[idx], msg)) return prev;
  const edited = { ...msg, edited: true };
  if (idx === -1) return [...prev, { ...edited, editOnly: true }];
  const next = [...prev];
  next[idx] = { ...edited, clientKey: prev[idx].clientKey ?? msg.clientKey };
  return next;
}

/**
 * Apply a realtime `messageDelete`: the live row (if any) is replaced by a tombstone, which also hides the
 * history row with the same id in the merge (and is never rendered itself).
 */
export function applyMessageDelete(prev: MockMessage[], id: string): MockMessage[] {
  const tombstone: MockMessage = {
    id,
    direction: "OUTBOUND",
    text: "",
    ageLabel: "",
    sender: { kind: "operator", name: "" },
    deleted: true,
    editOnly: true,
  };
  const idx = prev.findIndex((m) => m.id === id);
  if (idx === -1) return [...prev, tombstone];
  const next = [...prev];
  next[idx] = tombstone;
  return next;
}
